/**
 * Pure coordinate math for a responsive button grid - no Phaser
 * dependency, so it's directly unit-testable (see
 * `src/ui/__tests__/buttonLayout.test.ts`) without a Scene. Every
 * button-stack screen (`MenuScene`, `GameScene`'s game-over/level-
 * complete overlays, `DailyResultScene`) calls this to get placements,
 * then creates its own buttons (`createButton`/`reparentButton`) at
 * those coordinates - this module never touches Phaser/the DOM itself.
 *
 * `rows` is a list of "wants to be together" groups, each with 1-2
 * slots. A group with 2 live slots is placed side by side when
 * `availableWidth` comfortably fits both at >= `minTwoColumnWidth`
 * each; otherwise (or with only 1 live slot) every live slot in that
 * group stacks as its own full-width, centered row. A `null`/
 * `undefined` slot is simply skipped - the group reflows around it
 * (a lone survivor centers full-width; a fully-hidden group consumes no
 * vertical space at all) rather than leaving an empty gap.
 */

export interface ButtonSlot {
  /** Only used to distinguish "2 live slots" from "1 live slot that happens to be `!= null`" - carries no other meaning here. */
  readonly id?: string;
}

export interface PlacedButtonSlot {
  x: number;
  y: number;
  width: number;
  /** True when this button ended up side by side with another this row - callers typically use this to pick a smaller font size so longer labels still fit. */
  isTwoColumn: boolean;
}

export interface ButtonRowsLayoutConfig {
  /** Total horizontal space the grid may use, centered on x=0 - e.g. viewport width (or a panel's own width) minus side margins. */
  availableWidth: number;
  /** Y of the first row's center. */
  startY: number;
  /** Vertical center-to-center spacing between consecutive rows. */
  rowHeight: number;
  /** Horizontal gap between two side-by-side buttons. */
  columnGap?: number;
  /** Width a lone (1-column) button uses, capped to `availableWidth`. */
  singleColumnWidth?: number;
  /** Minimum width a button may have and still go 2-up - below this, the row falls back to stacking. */
  minTwoColumnWidth?: number;
  /** Upper bound on a 2-column button's width, so a very wide viewport doesn't stretch buttons absurdly. */
  maxTwoColumnWidth?: number;
}

export interface ButtonRowsLayoutResult {
  /** One entry per input slot, same shape/order as `rows` - `null` for a hidden slot. */
  placements: (PlacedButtonSlot | null)[][];
  /** Y just past the last row - where the caller can continue laying out whatever comes next. */
  bottomY: number;
}

const DEFAULTS = {
  columnGap: 14,
  singleColumnWidth: 240,
  minTwoColumnWidth: 140,
  maxTwoColumnWidth: 200,
};

export interface ContentFlowGapConfig {
  /** Minimum gap, used on the shortest supported viewport. */
  floor: number;
  /** Maximum gap, so a very tall viewport doesn't leave an oddly large empty band. */
  cap: number;
  /** `viewportHeight * factor` is the gap before clamping to `[floor, cap]` - the responsive part. */
  factor: number;
}

/**
 * The one place every button-stack screen turns "the real measured
 * bottom of my title/summary content" into "where the first button row
 * starts" - `contentBottom + gap`, where `gap` scales a little with
 * `viewportHeight` (a bit more breathing room on a spacious screen) but
 * is clamped to `[floor, cap]`, floored well above zero so it's never
 * negligible on a short screen.
 *
 * Deliberately just `contentBottom + gap` - nothing here ever caps or
 * clamps the RESULT against a fixed/height-fraction position. An
 * earlier version of this codebase computed a button grid's start Y as
 * `Math.min(heightFraction, Math.max(contentBottom + gap, otherFraction))` -
 * on a wide-but-short viewport (title text maxed out at its largest
 * clamped size) that outer `Math.min` could pull the result back UP
 * above `contentBottom`, overlapping the very content it was measuring.
 * `contentFlowStartYNeverBelowContentBottom` in
 * `buttonLayout.test.ts` asserts this can't happen again: for any
 * `contentBottom`/`viewportHeight`, the result is always
 * `>= contentBottom + config.floor`.
 */
export function contentFlowStartY(contentBottom: number, viewportHeight: number, config: ContentFlowGapConfig): number {
  const gap = Math.max(config.floor, Math.min(config.cap, viewportHeight * config.factor));
  return contentBottom + gap;
}

/**
 * `createButton()` (and `reparentButton()`) position a button's CENTER at
 * the Y they're given - so placing a button below some measured text by
 * handing it that text's own bottom-edge Y directly silently overlaps the
 * button's top half into the text by `buttonHeight / 2 - gap` whenever
 * that's positive. This was exactly the bug behind `EndlessLeaderboardScene`'s
 * SET NAME button overlapping its own explanatory text on mobile - worse
 * there specifically because a narrow viewport wraps that text to an
 * extra line, and the same fixed (buggy) offset then ate into that wrapped
 * second line rather than a line's harmless bottom margin.
 *
 * `textBottomY` should always be the text's own REAL measured bottom edge
 * (`text.y + text.height`, after any `wordWrap` has already been applied) -
 * never a value assumed for one specific viewport size, so this keeps
 * working whether the text above it wrapped to one line or several.
 */
export function buttonCenterYBelow(textBottomY: number, gap: number, buttonHeight: number): number {
  return textBottomY + gap + buttonHeight / 2;
}

export function layoutButtonRows(
  rows: readonly (readonly (ButtonSlot | null | undefined)[])[],
  config: ButtonRowsLayoutConfig,
): ButtonRowsLayoutResult {
  const columnGap = config.columnGap ?? DEFAULTS.columnGap;
  const singleColumnWidth = Math.min(config.singleColumnWidth ?? DEFAULTS.singleColumnWidth, config.availableWidth);
  const minTwoColumnWidth = config.minTwoColumnWidth ?? DEFAULTS.minTwoColumnWidth;
  const maxTwoColumnWidth = config.maxTwoColumnWidth ?? DEFAULTS.maxTwoColumnWidth;

  const rawHalf = (config.availableWidth - columnGap) / 2;
  const twoColumnWidth = Math.min(maxTwoColumnWidth, rawHalf);
  const canUseTwoColumns = rawHalf >= minTwoColumnWidth;

  const placements: (PlacedButtonSlot | null)[][] = [];
  let y = config.startY;

  for (const row of rows) {
    const liveCount = row.filter((slot) => slot != null).length;

    if (liveCount === 0) {
      placements.push(row.map(() => null));
      continue;
    }

    if (liveCount >= 2 && canUseTwoColumns) {
      const totalWidth = twoColumnWidth * 2 + columnGap;
      const xs = [-totalWidth / 2 + twoColumnWidth / 2, totalWidth / 2 - twoColumnWidth / 2];
      let liveIndex = 0;
      const rowPlacements = row.map((slot): PlacedButtonSlot | null => {
        if (slot == null) return null;
        const placed: PlacedButtonSlot = { x: xs[liveIndex], y, width: twoColumnWidth, isTwoColumn: true };
        liveIndex++;
        return placed;
      });
      placements.push(rowPlacements);
      y += config.rowHeight;
      continue;
    }

    // 1 column: every live slot in this group gets its own centered,
    // full-width row (normally there's only one live slot once
    // `canUseTwoColumns` is false or `liveCount < 2`, but a group
    // authored with 3+ slots - not expected today - degrades the same way).
    const rowPlacements: (PlacedButtonSlot | null)[] = [];
    for (const slot of row) {
      if (slot == null) {
        rowPlacements.push(null);
        continue;
      }
      rowPlacements.push({ x: 0, y, width: singleColumnWidth, isTwoColumn: false });
      y += config.rowHeight;
    }
    placements.push(rowPlacements);
  }

  return { placements, bottomY: y };
}
